import { useEffect, useState } from 'react'
import cx from 'classnames'
import dayjs from 'dayjs'

import Styles from './CampaignsDropdown.module.scss'
import { Button } from 'blaze-ui/Button'
import { ChevronDown2 } from 'blaze-ui/icons'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { Text } from 'blaze-ui/Text/Text'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useCampaignOptions } from 'hooks/queries/campaignOptions/useCampaignOptions'
import { collectResults } from 'utils/query'
import { CampaignDropdown } from 'screens/Calendar/WeekViewV2/BatchContentActions/Modals/NewPostModal/CampaignDropdown'
import { CampaignRow, OneOffCampaignRow } from 'components/CampaignRow/CampaignRow'

interface CampaignsDropdownProps {
  setCampaignId: (campaignId: number | undefined) => void
  selectedCampaignId?: number
}

export const CampaignsDropdown = ({ setCampaignId, selectedCampaignId }: CampaignsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { data: brandPlan } = useCurrentBrandPlan()
  const schedulesQuery = useCampaignOptions(
    {
      brandPlanId: brandPlan?.id ?? 0,
      start_date: dayjs().format('YYYY-MM-DD'),
      order_direction: 'asc',
      items: 20,
    },
    { enabled: !!brandPlan?.id },
  )
  const schedules = collectResults(schedulesQuery, (d) => d.campaign_options).filter(Boolean)
  const oneOffScheduleId = brandPlan?.one_off_schedule_id

  // Match the New Post modal: when the source post isn't in a campaign, default to "No Campaign"
  // (the workspace's one-off schedule) rather than leaving the selection empty.
  useEffect(() => {
    if (selectedCampaignId === undefined && oneOffScheduleId !== undefined) {
      setCampaignId(oneOffScheduleId)
    }
  }, [selectedCampaignId, oneOffScheduleId, setCampaignId])

  const selectedSchedule = schedules.find((schedule) => schedule.id === selectedCampaignId)
  const isOneOff = selectedCampaignId !== undefined && selectedCampaignId === oneOffScheduleId

  const renderTriggerContent = () => {
    if (isOneOff) return <OneOffCampaignRow />
    if (selectedSchedule) return <CampaignRow schedule={selectedSchedule} />

    return (
      <Text variant="secondary" lineClamp={1}>
        Select campaign
      </Text>
    )
  }

  return (
    <DropdownTrigger onOpenChange={setIsOpen} placement="bottom-start">
      <Button
        className={Styles.button}
        variant="secondary"
        fullWidth
        endIcon={() => (
          <div className={cx({ [Styles.reverse]: isOpen })}>
            <ChevronDown2 strokeWidth="1.15" />
          </div>
        )}
      >
        {renderTriggerContent()}
      </Button>
      {(close) => (
        <div className={Styles.panel}>
          <CampaignDropdown
            schedules={schedules}
            selectedScheduleId={selectedCampaignId}
            isLoading={schedulesQuery.isLoading}
            oneOffScheduleId={oneOffScheduleId}
            onSelect={(id) => {
              setCampaignId(id)
              close()
            }}
          />
        </div>
      )}
    </DropdownTrigger>
  )
}
